/**
 * PDF Export component (legacy name "DocxExport" — actually generates PDFs
 * via server-side PDFKit at /api/generate-pdf-accurate).
 */
import React, { useState, useEffect } from 'react';
import { normalizePersistedQuoteEditorState } from '@shared/quote-editor-state';
import { getQuoteFileStem } from '@shared/quote-naming';
import { useOfferStore } from '@/lib/store';
import { saveAndGetQuoteData, validateQuoteData, getStoredQuoteDataList } from '@/lib/quote-data-service';
import { databaseService } from '@/lib/database-service';
import { getLineNetAfterDiscount, normalizeVatRate } from '@/lib/quote-calculations';
import { Button } from '@/components/ui/button';
import { Check, FileDown, Loader2, Database, AlertTriangle, Copy, Plus, FileJson, Sparkles, Eye } from 'lucide-react';
import { PDFViewerModal } from './pdf-viewer-modal';
import { AIProgressModal } from './ai-progress-modal';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';

interface PdfSettingsMeta {
  logoFilename?: string;
  agbText?: string;
}

export default function DocxExport() {
  const { toast } = useToast();
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [pdfSuccess, setPdfSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pdfSettingsMeta, setPdfSettingsMeta] = useState<PdfSettingsMeta | null>(null);
  const [isExportingJson, setIsExportingJson] = useState(false);
  const [jsonSuccess, setJsonSuccess] = useState(false);
  const [aiOptimization, setAiOptimization] = useState<'off' | 'light' | 'max'>('off');

  // PDF Viewer Modal state
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [pdfFileName, setPdfFileName] = useState('');
  const [pdfMetadata, setPdfMetadata] = useState<{
    customerName?: string;
    offerNumber?: string;
    totalGross?: number;
    itemCount?: number;
    createdAt?: string;
  } | null>(null);

  // AI Progress Modal state
  const [showAiProgress, setShowAiProgress] = useState(false);
  const [savedQuoteDataForAi, setSavedQuoteDataForAi] = useState<any>(null);

  // Angebot overwrite dialog state
  const [showAngebotDialog, setShowAngebotDialog] = useState(false);
  const [existingAngebots, setExistingAngebots] = useState<any[]>([]);
  const [selectedOverwriteOption, setSelectedOverwriteOption] = useState<'new' | 'overwrite'>('new');
  const [selectedSlot, setSelectedSlot] = useState<number>(1);
  
  const quoteHeader = useOfferStore((state) => state.quoteHeader);
  const quoteItems = useOfferStore((state) => state.quoteItems);
  const positions = useOfferStore((state) => state.positions);
  const gewerkOrder = useOfferStore((state) => state.gewerkOrder);
  const discountAmount = useOfferStore((state) => state.discountAmount);
  const discountType = useOfferStore((state) => state.discountType);
  const discountApplicationMode = useOfferStore((state) => state.discountApplicationMode);
  const isDiscountAppliedToPrices = useOfferStore((state) => state.isDiscountAppliedToPrices);
  
  const hasLogoConfigured = Boolean(pdfSettingsMeta?.logoFilename);
  const hasAgbConfigured = Boolean(pdfSettingsMeta?.agbText && pdfSettingsMeta.agbText.trim().length > 0);
  const exportVatRate = normalizeVatRate(quoteHeader.mwstSatz);
  
  // Load PDF settings on mount
  useEffect(() => {
    const loadPdfSettings = async () => {
      try {
        const response = await fetch('/api/pdf-settings');
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setPdfSettingsMeta(data.data || null);
          }
        }
      } catch (error) {
        console.error('Failed to load PDF settings:', error);
      }
    };
    loadPdfSettings();
  }, []);
  
  // Check for existing angebots
  const checkExistingAngebots = async () => {
    try {
      const response = await getStoredQuoteDataList();
      if (response.success && response.data?.files) {
        setExistingAngebots(response.data.files);
        return response.data.files.length > 0;
      }
      return false;
    } catch (error) {
      console.error('Error checking existing angebots:', error);
      return false;
    }
  };

  // Handle PDF export click - check for existing angebots first
  const handlePDFExportClick = async () => {
    const hasExisting = await checkExistingAngebots();
    if (hasExisting) {
      setShowAngebotDialog(true);
    } else {
      await handlePDFExport();
    }
  };

  // Handle dialog confirmation
  const handleDialogConfirm = async () => {
    setShowAngebotDialog(false);
    await handlePDFExport();
  };

  // Main PDF Export function (consolidated)
  const handlePDFExport = async () => {
    try {
      setIsGeneratingPDF(true);
      setPdfSuccess(false);
      setError(null);

      console.log("Starting PDF export with subtotals...");

      // Validate quote data
      if (!validateQuoteData(quoteHeader, quoteItems)) {
        setError("Ungültige Angebotsdaten. Bitte prüfen Sie Ihre Eingaben.");
        setIsGeneratingPDF(false);
        return;
      }

      // Save quote data first
      const targetSlot = selectedOverwriteOption === 'overwrite' ? selectedSlot : undefined;
      const editorState = normalizePersistedQuoteEditorState(useOfferStore.getState());
      const savedQuoteData = await saveAndGetQuoteData(
        quoteHeader,
        quoteItems,
        positions,
        gewerkOrder,
        `Angebot_${quoteHeader.angebot_nr}.pdf`,
        targetSlot,
        editorState
      );

      // If AI optimization is enabled, use the progress modal
      if (aiOptimization !== 'off') {
        setSavedQuoteDataForAi(savedQuoteData);
        setShowAiProgress(true);
        // The rest will be handled by handleAiComplete
        return;
      }

      // No AI - generate PDF directly
      await generatePdfWithData(savedQuoteData);

    } catch (error) {
      console.error('Error exporting PDF:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      setError(`PDF-Fehler: ${errorMessage}`);
      toast({
        title: "PDF-Fehler",
        description: errorMessage,
        variant: "destructive",
      });
      setIsGeneratingPDF(false);
    }
  };

  // Generate PDF with saved quote data (shared between normal and AI paths)
  const generatePdfWithData = async (savedQuoteData: any) => {
    try {
      // Generate PDF with accurate subtotals via server
      const response = await fetch('/api/generate-pdf-accurate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          header: savedQuoteData.header,
          items: savedQuoteData.items,
          gewerkOrder: savedQuoteData.gewerkOrder,
          options: {
            agbText: pdfSettingsMeta?.agbText && pdfSettingsMeta.agbText.trim().length > 0
              ? pdfSettingsMeta.agbText
              : undefined,
            discountAmount,
            discountType,
            discountApplicationMode,
            vatRate: normalizeVatRate(savedQuoteData?.header?.mwstSatz),
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Server returned ${response.status}`);
      }

      // Get PDF blob
      const blob = await response.blob();
      showPdfPreview(blob, savedQuoteData);

      // Create database entry for reference
      await createDatabaseEntry(savedQuoteData);

    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Show PDF preview modal
  const showPdfPreview = (blob: Blob, savedQuoteData: any) => {
    const customerName = quoteHeader.kunde?.trim() || 'Kunde';
    const offerNumber = quoteHeader.angebot_nr?.trim() || `A-${new Date().getFullYear()}-XXX`;
    const fileName = `${getQuoteFileStem({ header: quoteHeader }, 'Angebot')}.pdf`;

    const vatRate = normalizeVatRate(quoteHeader.mwstSatz);
    const netTotalBeforeGlobalDiscount = quoteItems.reduce((sum, item) => sum + getLineNetAfterDiscount(item), 0);
    const hasGlobalDiscount = discountApplicationMode === 'total' && !isDiscountAppliedToPrices && discountAmount > 0;
    const globalDiscountValueRaw = hasGlobalDiscount
      ? (discountType === 'percent'
        ? (netTotalBeforeGlobalDiscount * discountAmount) / 100
        : discountAmount)
      : 0;
    const globalDiscountValue = Math.max(0, Math.min(netTotalBeforeGlobalDiscount, Math.round(globalDiscountValueRaw * 100) / 100));
    const netTotal = Math.max(0, Math.round((netTotalBeforeGlobalDiscount - globalDiscountValue) * 100) / 100);
    const totalTax = Math.round(netTotal * (vatRate / 100) * 100) / 100;
    const grossTotal = Math.round((netTotal + totalTax) * 100) / 100;

    // Set state for PDF Viewer Modal
    setPdfBlob(blob);
    setPdfFileName(fileName);
    setPdfMetadata({
      customerName,
      offerNumber,
      totalGross: grossTotal,
      itemCount: quoteItems.length,
      createdAt: new Date().toISOString(),
    });
    setShowPdfViewer(true);

    setPdfSuccess(true);
    toast({
      title: "PDF erstellt",
      description: "Vorschau wird angezeigt - von dort herunterladen oder drucken.",
      variant: "success",
    });

    setTimeout(() => setPdfSuccess(false), 3000);
  };

  // Handle AI optimization completion
  const handleAiComplete = async (result: { success: boolean; pdfBase64?: string; error?: string }) => {
    setShowAiProgress(false);
    setIsGeneratingPDF(false);

    if (result.success && result.pdfBase64) {
      // Convert base64 to blob
      const byteCharacters = atob(result.pdfBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });

      showPdfPreview(blob, savedQuoteDataForAi);

      // Create database entry
      if (savedQuoteDataForAi) {
        await createDatabaseEntry(savedQuoteDataForAi);
      }
    } else {
      setError(result.error || 'KI-Optimierung fehlgeschlagen');
      toast({
        title: "KI-Optimierung fehlgeschlagen",
        description: result.error || 'Unbekannter Fehler',
        variant: "destructive",
      });
    }

    setSavedQuoteDataForAi(null);
  };

  // JSON Export function
  const handleJSONExport = async () => {
    try {
      setIsExportingJson(true);
      setJsonSuccess(false);
      setError(null);
      
      // Validate quote data
      if (!validateQuoteData(quoteHeader, quoteItems)) {
        setError("Ungültige Angebotsdaten. Bitte prüfen Sie Ihre Eingaben.");
        return;
      }
      
      // Build JSON export object
      const exportData = {
        header: quoteHeader,
        items: quoteItems.map(item => ({
          id: item.id,
          originalId: item.originalId,
          gewerk: item.gewerk,
          name: item.name,
          beschreibung: item.beschreibung,
          einheit: item.einheit,
          preis: item.preis,
          menge: item.menge,
          umsatzsteuer: item.umsatzsteuer,
          gesamtpreis: item.preis * item.menge,
        })),
        gewerkOrder: gewerkOrder,
        metadata: {
          exportDate: new Date().toISOString(),
          totalItems: quoteItems.length,
          totalNetValue: quoteItems.reduce((sum, item) => sum + (item.preis * item.menge), 0),
          version: '1.0',
        }
      };
      
      // Create and download JSON file
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const offerNumber = quoteHeader.angebot_nr?.trim() || `Angebot-${Date.now()}`;
      link.download = `${offerNumber}.json`;
      link.click();
      URL.revokeObjectURL(url);
      
      setJsonSuccess(true);
      toast({
        title: "JSON exportiert",
        description: `"${offerNumber}.json" wurde heruntergeladen.`,
        variant: "success",
      });
      
      setTimeout(() => setJsonSuccess(false), 3000);
    } catch (error) {
      console.error('Error exporting JSON:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      setError(`JSON-Fehler: ${errorMessage}`);
      toast({
        title: "JSON-Fehler",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsExportingJson(false);
    }
  };

  // Create database entry after export
  const createDatabaseEntry = async (savedQuoteData: any) => {
    try {
      const positionsWithCurrentQuantities = positions.map(position => {
        const quoteItem = quoteItems.find(item => item.id === position.id);
        const currentQuantity = quoteItem ? quoteItem.menge : 0;
        return { ...position, menge: currentQuantity };
      });
      
      const databaseEntries = positionsWithCurrentQuantities.map((position, index) => {
        const preis = typeof position.preis === 'string' ? parseFloat(position.preis) || 0 : position.preis;
        return {
          artikelId: position.originalId || position.id?.toString() || (index + 1).toString(),
          gewerk: position.gewerk || '',
          name: position.name || '',
          beschreibung: position.beschreibung || '',
          einheit: position.einheit || 'Stk',
          preis: preis,
          einzelpreis: preis,
          menge: position.menge || 0,
          mwst: position.umsatzsteuer || 19,
        };
      });

      const projectInfo = {
        projectNumber: savedQuoteData.header.angebot_nr || `QUOTE-${Date.now()}`,
        projectName: `${savedQuoteData.header.angebot_nr} - ${savedQuoteData.header.kunde}`,
        location: savedQuoteData.header.anschrift || '',
        description: `Angebot für ${savedQuoteData.header.kunde}`,
        date: savedQuoteData.header.datum || new Date().toISOString().split('T')[0]
      };

      const sourceInfo = {
        createdBy: 'quote-builder' as const,
        exportDate: new Date().toISOString(),
        totalValue: positionsWithCurrentQuantities.reduce((sum, pos) => {
          const preis = typeof pos.preis === 'string' ? parseFloat(pos.preis) || 0 : pos.preis;
          return sum + (preis * pos.menge);
        }, 0)
      };

      await databaseService.createDatabase(projectInfo, databaseEntries, sourceInfo);
    } catch (dbError) {
      console.error('Database creation failed:', dbError);
      // Don't fail export if database creation fails
    }
  };
  
  return (
    <div className="space-y-3">
      {/* Main PDF Export Button */}
      <Button
        onClick={handlePDFExportClick}
        disabled={isGeneratingPDF || quoteItems.length === 0}
        className="w-full bg-blue-600 hover:bg-blue-700"
        data-testid="button-export-pdf"
      >
        {isGeneratingPDF ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {aiOptimization !== 'off' ? 'KI optimiert...' : 'PDF wird erstellt...'}
          </>
        ) : pdfSuccess ? (
          <>
            <Check className="mr-2 h-4 w-4" />
            PDF bereit
          </>
        ) : (
          <>
            <Eye className="mr-2 h-4 w-4" />
            PDF Vorschau
          </>
        )}
      </Button>

      {/* AI Optimization Option */}
      <div className="flex items-center gap-2">
        <Sparkles className="h-3 w-3 text-amber-500" />
        <Select
          value={aiOptimization}
          onValueChange={(v) => setAiOptimization(v as 'off' | 'light' | 'max')}
          disabled={isGeneratingPDF}
        >
          <SelectTrigger className="h-7 text-xs flex-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="off">Keine KI-Optimierung</SelectItem>
            <SelectItem value="light">KI: Leichte Optimierung</SelectItem>
            <SelectItem value="max">KI: MAX (Restrukturierung)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {aiOptimization === 'max' && (
        <p className="text-xs text-orange-600 flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" />
          MAX strukturiert Texte um und kürzt lange Beschreibungen
        </p>
      )}

      {/* Error display */}
      {error && (
        <div className="text-red-500 text-sm">
          {error}
        </div>
      )}
      
      {/* Angebot Overwrite Dialog */}
      {showAngebotDialog && (
        <Dialog open={showAngebotDialog} onOpenChange={setShowAngebotDialog}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-orange-500" />
                Angebot speichern
              </DialogTitle>
              <DialogDescription>
                Es existieren bereits gespeicherte Angebote. Möchten Sie ein neues Angebot erstellen oder ein bestehendes überschreiben?
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-3">
                <Label className="text-sm font-medium">Speicher-Option wählen:</Label>
                
                <div className="space-y-2">
                  <label className="flex items-start space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="saveOption"
                      value="new"
                      checked={selectedOverwriteOption === 'new'}
                      onChange={(e) => setSelectedOverwriteOption(e.target.value as 'new' | 'overwrite')}
                      className="mt-1"
                    />
                    <div>
                      <div className="flex items-center gap-2">
                        <Plus className="h-4 w-4 text-green-600" />
                        <span className="font-medium text-green-700">Neues Angebot erstellen</span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        Erstellt ein neues Angebot (empfohlen)
                      </p>
                    </div>
                  </label>
                  
                  <label className="flex items-start space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="saveOption"
                      value="overwrite"
                      checked={selectedOverwriteOption === 'overwrite'}
                      onChange={(e) => setSelectedOverwriteOption(e.target.value as 'new' | 'overwrite')}
                      className="mt-1"
                    />
                    <div>
                      <div className="flex items-center gap-2">
                        <Copy className="h-4 w-4 text-orange-600" />
                        <span className="font-medium text-orange-700">Bestehendes überschreiben</span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        Überschreibt ein ausgewähltes Angebot
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              {selectedOverwriteOption === 'overwrite' && existingAngebots.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Angebot auswählen:</Label>
                  <Select
                    value={selectedSlot.toString()}
                    onValueChange={(value) => setSelectedSlot(Number(value))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Angebot auswählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {existingAngebots.map((angebot) => (
                        <SelectItem key={angebot.slot} value={angebot.slot.toString()}>
                          <span className="font-medium">{angebot.offerNumber || `Slot ${angebot.slot}`}</span>
                          <span className="text-sm text-gray-500 ml-2">
                            {angebot.customerName}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <div className="flex items-start gap-2">
                  <Database className="h-4 w-4 text-blue-600 mt-0.5" />
                  <p className="text-sm text-blue-700">
                    Nach dem Export wird automatisch eine Angebots-Datenbank erstellt.
                  </p>
                </div>
              </div>
            </div>
            
            <DialogFooter className="flex gap-2">
              <Button variant="outline" onClick={() => setShowAngebotDialog(false)}>
                Abbrechen
              </Button>
              <Button onClick={handleDialogConfirm} className="bg-blue-600 hover:bg-blue-700">
                {selectedOverwriteOption === 'new' ? 'Neues Angebot' : 'Überschreiben'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* AI Progress Modal */}
      {showAiProgress && savedQuoteDataForAi && (
        <AIProgressModal
          isOpen={showAiProgress}
          items={savedQuoteDataForAi.items}
          header={savedQuoteDataForAi.header}
          gewerkOrder={savedQuoteDataForAi.gewerkOrder}
          aiMode={aiOptimization as 'light' | 'max'}
          options={{
            agbText: pdfSettingsMeta?.agbText && pdfSettingsMeta.agbText.trim().length > 0
              ? pdfSettingsMeta.agbText
              : undefined,
            discountAmount,
            discountType,
            discountApplicationMode,
            vatRate: exportVatRate,
          }}
          onComplete={handleAiComplete}
        />
      )}

      {/* PDF Viewer Modal */}
      <PDFViewerModal
        isOpen={showPdfViewer}
        onClose={() => setShowPdfViewer(false)}
        pdfBlob={pdfBlob}
        fileName={pdfFileName}
        metadata={pdfMetadata || undefined}
      />
    </div>
  );
}
